import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";
import _ from "underscore";

import type {StyleType} from "@khanacademy/wonder-blocks-core";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {publishAnalyticsEventForMinorConversion} from "@khan/analytics-learning-time";
import {GuideStateApi, useGuideState} from "@khan/guide-state";
import {
    ActionTypes,
    type ActionBubble as ActionBubbleType,
} from "@khan/guide-types";
import {mediaQueries} from "@khan/shared-styles";

import ActionBubble from "./action-bubble.tsx";

interface Props {
    actionBubbles: ReadonlyArray<ActionBubbleType>;
    // We may want to disable these bubbles, EG when the bot is already
    // processing another message from the user.
    disabled?: boolean;
    // Whether these bubbles are attached to the most recent message in
    // the chat.
    isMostRecentMessage?: boolean;
    interactionId?: string;
    style?: StyleType;
    onActionSelected: (action: ActionBubbleType) => void;
    dabIds?: readonly string[];
}

/**
 * A component to display a list of "prebaked prompts", intended to let the
 * user click a button to send a prepopulated question to the bot.
 *
 * For example, "Tell me more about this article," or "Show me an example"
 * could be suggested prompts for the user to send to the bot.
 */
const ActionBubbles = (props: Props): React.ReactElement => {
    const {
        actionBubbles,
        disabled,
        style,
        onActionSelected,
        isMostRecentMessage,
        interactionId,
        dabIds,
    } = props;

    const open = useGuideState("open");

    // This function is memoized so that it is only called once per set
    // of action bubbles. If the user closes and opens the guide, we
    // do not want this to be called again.
    const sendDynamicActionBubblesDisplayedEvent = React.useMemo(
        () =>
            _.once(() => {
                const threadId = GuideStateApi.latest().threadId;
                if (interactionId && threadId) {
                    publishAnalyticsEventForMinorConversion({
                        eventType: "AiGuideDynamicActionBubblesDisplayed",
                        eventSchemaVersion: 2,
                        aiGuideThreadId: threadId,
                        aiGuideInteractionId: interactionId,
                        aiGuideNavigationId:
                            GuideStateApi.latest().navigation.id,
                    });
                }
            }),
        // We want a new function to be created if the interactionId changes
        [interactionId],
    );

    // We only want to send the DynamicActionBubblesDisplayedEvent
    // if the user *sees* the DAB, so we only send it if there are
    // DAB and the guide is open. Also add a check for isMostRecentMessage
    // to ensure we're only firing once per message.
    //
    // If the user sends a new message while the DAB are still generating,
    // the request to generate them will be cancelled and they will never
    // be shown, so we don't have to worry about that case.
    React.useEffect(() => {
        if (
            open &&
            actionBubbles.some((action) => action.isDynamic) &&
            isMostRecentMessage
        ) {
            sendDynamicActionBubblesDisplayedEvent();
        }
    }, [
        open,
        actionBubbles,
        isMostRecentMessage,
        sendDynamicActionBubblesDisplayedEvent,
    ]);

    // Getting mutable copy of dabIds to pop from. We don't want to mutate
    // the prop directly to avoid re-renders.
    // The ids are used for aria-controls on the DAB toggle.
    const dabIdsArr = dabIds ? [...dabIds] : [];

    const supportedActionsBubbles = React.useMemo(() => {
        return filterUnsupportedActionBubbles(actionBubbles);
    }, [actionBubbles]);

    return (
        <fieldset
            className={css(styles.fieldset)}
            style={style as React.CSSProperties}
        >
            <legend className={css(styles.legend)}>
                {t`Additional actions available:`}
            </legend>
            <div aria-live="polite" className={css(styles.actionBubbles)}>
                {supportedActionsBubbles.map((actionBubble, index) => (
                    <ActionBubble
                        key={index}
                        actions={actionBubble.actions}
                        disabled={disabled}
                        isOnMostRecentMessage={isMostRecentMessage}
                        label={actionBubble.label}
                        oldMessageBehavior={actionBubble.oldMessageBehavior}
                        isDynamic={actionBubble.isDynamic}
                        variant={actionBubble.variant}
                        onActionSelected={() => onActionSelected(actionBubble)}
                        id={
                            actionBubble.isDynamic ? dabIdsArr.pop() : undefined
                        }
                    />
                ))}
            </div>
        </fieldset>
    );
};

function filterUnsupportedActionBubbles(
    actionBubbles: ReadonlyArray<ActionBubbleType>,
): ReadonlyArray<ActionBubbleType> {
    return actionBubbles.filter((actionBubble) => {
        return (
            // filter out the action bubbles that are not currently supported on the FE (this can happen during runtime)
            // allow the bubble if there is no actions array (this can happen at runtime)
            !actionBubble.actions ||
            actionBubble.actions.every((action) =>
                ActionTypes.includes(action.actionType),
            )
        );
    });
}

const styles = StyleSheet.create({
    fieldset: {
        border: "none",
        margin: 0,
        padding: 0,
    },
    legend: {
        // Hide the legend visually but keep it accessible to screen readers
        position: "absolute",
        width: "1px",
        height: "1px",
        padding: 0,
        margin: "-1px",
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        whiteSpace: "nowrap",
        border: 0,
    },
    actionBubbles: {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        gap: spacing.xSmall_8,
        [mediaQueries.smOrSmaller]: {
            // On small screens, we want the bubbles to stack vertically.
            flexDirection: "column",
        },
    },
});

export {ActionBubbles};
// eslint-disable-next-line react-refresh/only-export-components -- porting from old code.
export const exportedForTesting = {
    filterUnsupportedActionBubbles,
};
